import store from "@/store"
import {
  AdminItem,
  AdminLeaderboard,
  BanWord,
  BlockUserPayload,
  Broadcast,
  CasinoGameStatistic,
  ChatUserBan,
  Configs,
  GiveawayType,
  LiveWinnerType,
  Moderator,
  Referral,
  ReferralsInfo,
  TokenData,
  UserData,
} from "@/store/types"
import Axios, { AxiosError, AxiosResponse } from "axios"
import {
  ApiResponse,
  Bot,
  BotItem,
  CasinoGame,
  CoinFlipRoom,
  CreateGiveaway,
  Game,
  GetRoomsQuery,
  Inventory,
  LeaderboardResponse,
  RoomCreateItem,
  UpgradeItemsConfigs,
} from "./types"
import { CasinoGameSlug, GameSlug, GiveawayStatus } from "./enums"
import { WithdrawResponse } from "@/components/withdraw/desktop/types"
import { privateSocket } from "@/plugins/socket"
const axios = Axios.create()

export const REFRESH_STATE = {
  refreshing: false,
  promise: null as Promise<string> | null,
}

export const callApi = {
  setDefaults() {
    axios.defaults.headers.common = {
      Accept: "*",
      "Content-Type": "application/json",
    }

    axios.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem(
      "access_token"
    )}`

    axios.defaults.withCredentials = false
  },

  getUserInfo() {
    this.setDefaults()

    return axios.get<ApiResponse<UserData>>("/api/auth/profile")
  },

  getTrades() {
    return axios.get("/api/trades")
  },
  getCategories(userId: number) {
    return axios.get(`/api/${userId}/categories`)
  },
  getUsers<UserType>(
    search: string | null,
    nextPageUrl?: string,
    perPage = 10
  ) {
    let query = `per_page=${perPage}`
    if (search) {
      query += `&q=${search}`
    }
    return axios.get<ApiResponse<UserType[]>>(
      nextPageUrl ? `${nextPageUrl}&${query}` : `/api/users?${query}`
    )
  },
  getAccessToken(query: unknown) {
    return axios.get("/api/auth/callback", {
      params: query,
    })
  },

  getCategory(userId: number, categoryId: number) {
    return axios.get(`/api/${userId}/categories/${categoryId}`)
  },

  getGames() {
    return axios.get<ApiResponse<Game[]>>("/api/games")
  },

  getCasinoGame<TConfig = Record<string, any>>(slug: CasinoGameSlug) {
    return axios.get<ApiResponse<CasinoGame<TConfig>>>(
      `/api/casino-games/${slug}`
    )
  },

  getGameBots(game_slug: GameSlug) {
    return axios.get<ApiResponse<Bot[]>>(`/api/games/${game_slug}/bots`)
  },
  getWithdrawalAvailableBots(game_slug: GameSlug, withdrawal_id: number) {
    return axios.get<ApiResponse<Bot[]>>(
      `/api/games/${game_slug}/withdrawals/${withdrawal_id}/available-bots`
    )
  },
  getPendingWithdrawals(game_slug: GameSlug) {
    return axios.get<ApiResponse<[]>>(`/api/games/${game_slug}/withdrawals`)
  },
  cancelWithdrawal(game_slug: GameSlug, withdrawal_id: number) {
    return axios.post<ApiResponse<[]>>(
      `/api/games/${game_slug}/withdrawals/${withdrawal_id}/cancel`
    )
  },
  async proceedWithdrawal(
    game_slug: GameSlug,
    withdrawal_id: number,
    bot_id: number
  ) {
    return axios.post<ApiResponse<WithdrawResponse>>(
      `/api/games/${game_slug}/withdrawals/${withdrawal_id}/proceed`,
      {
        bot_id,
      }
    )
  },
  getGameInventory<TGame extends GameSlug>(
    game_slug: GameSlug,
    params?: {
      min?: number
      page?: number
      per_page?: number
      search?: string
      expand_count?: boolean
      order?: "asc" | "desc" | "alpha"
    }
  ) {
    return axios.get<ApiResponse<Inventory<TGame>[]>>(
      `/api/games/${game_slug}/inventory`,
      {
        params,
      }
    )
  },
  getUpgraderSmartBet<TGame extends GameSlug>(
    game_slug: GameSlug,
    params?: {
      bet: number
      fast_bet: number | string
    }
  ) {
    return axios.post<ApiResponse<Inventory<TGame>[]>>(
      `/api/games/${game_slug}/upgrader/smart-bet`,
      {
        ...params,
      }
    )
  },

  getBloxupInventory<TGame extends GameSlug>(
    game_slug: GameSlug,
    params?: {
      min?: string | number
      page?: number
      per_page?: number
      search?: string
      order?: "asc" | "desc" | "alpha"
    }
  ) {
    return axios.get<ApiResponse<Inventory<TGame>[]>>(
      `/api/games/${game_slug}/upgrades`,
      {
        params,
      }
    )
  },

  upgradeItems(
    game_slug: GameSlug,
    casino_game_slug: CasinoGameSlug,
    items: RoomCreateItem[],
    configs: UpgradeItemsConfigs
  ) {
    return axios.post<ApiResponse<CoinFlipRoom>>(
      `/api/games/${game_slug}/casino-games/${casino_game_slug}/rooms`,
      {
        items,
        configs,
      }
    )
  },

  getLiveWinningItems(casino_game_slug: CasinoGameSlug) {
    return axios.get<ApiResponse<Inventory<GameSlug>[]>>(
      `/api/casino-games/${casino_game_slug}/live-winning-items`
    )
  },

  getCookie() {
    return axios.get("https://roblox.com", {
      withCredentials: true,
    })
  },
  getPhrase(username: string, privacy_and_policy: boolean) {
    return axios.post("/api/auth/get-phrase", {
      username,
      privacy_and_policy,
    })
  },
  authorize(username: string, referral_id?: number) {
    return axios.post("/api/auth/authorize", {
      username,
      referral_id,
    })
  },
  login(username: string, password: string) {
    return axios.post("/api/auth/login", {
      username,
      password,
    })
  },
  getRooms<TRoom>(
    game_slug: GameSlug,
    casino_game_slug: string,
    nextPageUrl?: string,
    query?: GetRoomsQuery
  ) {
    return axios.get<ApiResponse<TRoom[]>>(
      nextPageUrl
        ? nextPageUrl
        : `/api/games/${game_slug}/casino-games/${casino_game_slug}/rooms`,
      {
        params: query,
      }
    )
  },
  getRoom<TRoom>(
    game_slug: GameSlug,
    casino_game_slug: string,
    roomId: number
  ) {
    return axios.get<ApiResponse<TRoom>>(
      `/api/games/${game_slug}/casino-games/${casino_game_slug}/rooms/${roomId}`
    )
  },

  leaveRoom<TRoom>(
    game_slug: GameSlug,
    casino_game_slug: string,
    room_id: number
  ) {
    return axios.post<ApiResponse<TRoom>>(
      `/api/games/${game_slug}/casino-games/${casino_game_slug}/rooms/${room_id}/leave`
    )
  },

  inviteBot<TRoom>(
    game_slug: GameSlug,
    casino_game_slug: string,
    room_id: number
  ) {
    return axios.post<ApiResponse<TRoom>>(
      `/api/games/${game_slug}/casino-games/${casino_game_slug}/rooms/${room_id}/invite-bot`
    )
  },

  joinRoom<TRoom>(
    room_id: number,
    game_slug: GameSlug,
    casino_game_slug: CasinoGameSlug,
    items: RoomCreateItem[]
  ) {
    return axios.post<ApiResponse<TRoom>>(
      `/api/games/${game_slug}/casino-games/${casino_game_slug}/rooms/${room_id}/join`,
      {
        items,
      }
    )
  },
  updateItems<TRoom>(
    room_id: number,
    game_slug: GameSlug,
    casino_game_slug: CasinoGameSlug,
    items: RoomCreateItem[]
  ) {
    return axios.post<ApiResponse<TRoom>>(
      `/api/games/${game_slug}/casino-games/${casino_game_slug}/rooms/${room_id}/update-items`,
      {
        items,
      }
    )
  },
  withdrawGame(
    game_slug: GameSlug,
    items: { item_id: number; count: number }[]
  ) {
    return axios.post<ApiResponse<{ result: string }>>(
      `/api/games/${game_slug}/withdrawals`,
      {
        items,
      }
    )
  },
  createRoom<T>(
    game_slug: GameSlug,
    casino_game_slug: CasinoGameSlug,
    items: RoomCreateItem[],
    configs: T
  ) {
    return axios.post<ApiResponse<CoinFlipRoom>>(
      `/api/games/${game_slug}/casino-games/${casino_game_slug}/rooms`,
      {
        configs,
        items,
      }
    )
  },
  transferItem(
    user_id: number,
    game_slug: GameSlug,
    itemId: number,
    count: number
  ) {
    return axios.post<ApiResponse<[]>>(
      `/api/games/${game_slug}/inventory/${itemId}/transfer`,
      {
        user_id,
        count,
      }
    )
  },
  async refreshToken() {
    if (REFRESH_STATE.refreshing) {
      return await REFRESH_STATE.promise
    }

    REFRESH_STATE.refreshing = true

    const refresh_token = localStorage.getItem("refresh_token")

    try {
      const response = await axios.post("/api/auth/refresh", {
        refresh_token,
      })
      if (!response.data.error) {
        Object.keys(response.data).forEach((key) => {
          localStorage.setItem(key, response.data[key])
        })

        await store.dispatch("auth/setTokenData", response.data)
        callApi.setDefaults()
        return response.data.access_token
      }
    } finally {
      setTimeout(() => {
        REFRESH_STATE.refreshing = false
        REFRESH_STATE.promise = null
      }, 3000)
    }
  },
  async logout() {
    try {
      const refreshToken = localStorage.getItem("refresh_token")

      if (refreshToken) {
        await axios.post("/api/auth/logout", {
          refresh_token: refreshToken,
        })
      }

      return true
    } catch (error) {
      return false
    }
  },
  getLeaderboard(game_slug: GameSlug, period: string) {
    return axios.get<LeaderboardResponse>(
      `/api/leaderboard/games/${game_slug}?period=${period}`
    )
  },
  getReferralByCode(referralCode: string) {
    return axios.get<ApiResponse<UserData>>(`/api/referrals/${referralCode}`)
  },
  getReferrals() {
    return axios.get<ApiResponse<Referral[]>>(`/api/referrals/`)
  },
  getReferralsInfo() {
    return axios.get<ApiResponse<ReferralsInfo>>(`/api/referrals/info`)
  },
  getConfigs() {
    return axios.get<ApiResponse<Configs>>(`/api/configs`)
  },
  getLiveWinners() {
    return axios.get<ApiResponse<LiveWinnerType[]>>(`/api/live-winners`)
  },
  getCasinoGameStatistics(game_slug: GameSlug) {
    return axios.get<ApiResponse<CasinoGameStatistic[]>>(
      `/api/games/${game_slug}/statistics`
    )
  },
  admin: {
    serverInventory<TGame extends GameSlug>(
      game_slug: GameSlug,
      params?: { min: number }
    ) {
      return axios.get<ApiResponse<Inventory<TGame>[]>>(
        `/api/admin/games/${game_slug}/server-inventory`,
        {
          params,
        }
      )
    },
    bots() {
      return axios.get("/api/admin/bots")
    },
    checkIp() {
      return axios.get("/api/admin/check-ip")
    },
    items(game_slug: GameSlug) {
      return axios.get<ApiResponse<AdminItem[]>>(
        `/api/admin/games/${game_slug}/items`
      )
    },
    leaderboards() {
      return axios.get<ApiResponse<AdminLeaderboard[]>>(
        `/api/admin/leaderboards`
      )
    },
    getLeaderboard(id: number) {
      return axios.get<ApiResponse<AdminLeaderboard>>(
        `/api/admin/leaderboards/${id}`
      )
    },
    getAvailableGameItems(game_slug: GameSlug, filters: object = {}) {
      return axios.get<ApiResponse<BotItem[]>>(
        `/api/admin/games/${game_slug}/available-prizes`,
        {
          params: filters,
        }
      )
    },
    updateLeaderboard(
      id: number,
      payload: { prizes: string[]; status: string }
    ) {
      return axios.put<ApiResponse<AdminLeaderboard>>(
        `/api/admin/leaderboards/${id}`,
        payload
      )
    },
    updateItem(game_slug: GameSlug, item_id: number, data: { value: number }) {
      return axios.patch<ApiResponse<AdminItem>>(
        `/api/admin/games/${game_slug}/items/${item_id}`,
        data
      )
    },
    getBanWords() {
      return axios.get<ApiResponse<BanWord[]>>("/api/admin/ban-words")
    },
    createBanWord({ word }: { word: string }) {
      return axios.post<ApiResponse<BanWord>>("/api/admin/ban-words", {
        word,
      })
    },
    deleteBanWord(id: number) {
      return axios.delete(`/api/admin/ban-words/${id}`)
    },
    fetchBroadcasts() {
      return axios.get<AxiosResponse<Broadcast[]>>(
        `/api/admin/system-broadcasts`
      )
    },
    createBroadcast(broadcast: Partial<Broadcast>) {
      return axios.post<AxiosResponse<Broadcast>>(
        `/api/admin/system-broadcasts`,
        broadcast
      )
    },
    updateBroadcast(updated: Partial<Broadcast>) {
      return axios.put<AxiosResponse<Broadcast>>(
        `/api/admin/system-broadcasts/${updated.id}`,
        updated
      )
    },
    deleteBroadcast(broadcast: Broadcast) {
      return axios.delete<ApiResponse<Broadcast>>(
        `/api/admin/system-broadcasts/${broadcast.id}`
      )
    },
    fetchModerators() {
      return axios.get<ApiResponse<Moderator[]>>("/api/admin/moderators")
    },

    createModerator(payload: FormData) {
      return axios.post<ApiResponse<Moderator>>(
        "/api/admin/moderators",
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
    },

    updateModerator(id: number, payload: FormData) {
      return axios.post<ApiResponse<Moderator>>(
        `/api/admin/moderators/${id}`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
    },

    deleteModerator(id: number) {
      return axios.delete<ApiResponse<null>>(`/api/admin/moderators/${id}`)
    },
    getBlockedUsers() {
      return axios.get<ApiResponse<ChatUserBan[]>>(
        `/api/admin/chat/users/blocked`
      )
    },
    blockUserChat(user_id: number, payload: BlockUserPayload) {
      return axios.post<ApiResponse<object>>(
        `/api/admin/chat/users/${user_id}/block`,
        payload
      )
    },
    unblockUserChat(user_id: number, ban_id: number) {
      return axios.post<ApiResponse<object>>(
        `/api/admin/chat/users/${user_id}/bans/${ban_id}/unblock`
      )
    },
    fetchGames: () => axios.get<AxiosResponse<Game[]>>("/api/admin/games"),
    fetchBots: () => axios.get<AxiosResponse<Bot[]>>("/api/admin/bots"),
    createBot: (b: Partial<Bot>) =>
      axios.post<AxiosResponse<Bot>>("/api/admin/bots", b),
    updateBot: (b: Partial<Bot>) =>
      axios.put<AxiosResponse<Bot>>(`/api/admin/bots/${b.id}`, b),
    deleteBot: (b: Bot) => axios.delete(`/api/admin/bots/${b.id}`),
    startBot: (b: Bot) =>
      axios.post<AxiosResponse<Bot>>(`/api/admin/bots/${b.id}/start`),
    stopBot: (b: Bot) =>
      axios.post<AxiosResponse<Bot>>(`/api/admin/bots/${b.id}/stop`),
  },
  moderator: {
    blockUserChat(user_id: number, payload: BlockUserPayload) {
      return axios.post<ApiResponse<object>>(
        `/api/chat/users/${user_id}/block`,
        payload
      )
    },
    unblockUserChat(user_id: number, ban_id: number) {
      return axios.post<ApiResponse<object>>(
        `/api/chat/users/${user_id}/bans/${ban_id}/unblock`
      )
    },
    getBlockedUsers() {
      return axios.get<ApiResponse<ChatUserBan[]>>(`/api/chat/users/blocked`)
    },
  },
  giveaway: {
    get(game_slug: GameSlug, status?: GiveawayStatus) {
      return axios.get<ApiResponse<GiveawayType[]>>(
        `/api/games/${game_slug}/giveaways`,
        {
          params: {
            status: status,
          },
        }
      )
    },
    post(game_slug: GameSlug, giveaway: CreateGiveaway) {
      return axios.post<ApiResponse<GiveawayType>>(
        `/api/games/${game_slug}/giveaways`,
        giveaway
      )
    },
    join(giveaway: GiveawayType, user_id: number) {
      return axios.post<ApiResponse<GiveawayType>>(
        `/api/games/${giveaway.game_slug}/giveaways/${giveaway.id}/participants`,
        {
          user_id,
        }
      )
    },
  },
}
callApi.setDefaults()

let isRefreshing = false

type FailedRequest = {
  resolve: (token: string) => void
  reject: (error: AxiosError) => void
}

let failedQueue: FailedRequest[] = []

const processQueue = (error: AxiosError | null, token = ""): void => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })

  failedQueue = []
}
axios.defaults.baseURL = process.env.VUE_APP_API_URL
export const refreshSocketToken = (newToken: string) => {
  if (privateSocket) {
    // privateSocket.disconnect()
    privateSocket.auth = { token: newToken } // обновляем токен
    privateSocket.connect()
  }
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const status = error.response ? error.response.status : null

    if (status === 401 && !originalRequest._retry) {
      if (originalRequest.url === "/api/auth/refresh") {
        await store.dispatch("auth/logout")
      }

      const refresh_token = localStorage.getItem("refresh_token")

      if (!refresh_token) {
        localStorage.removeItem("access_token")
        window.location.href = "/"
        return
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest._retry = true
              originalRequest.headers.Authorization = `Bearer ${token}`
              resolve(axios(originalRequest))
            },
            reject,
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        if (!REFRESH_STATE.refreshing) {
          REFRESH_STATE.promise = callApi.refreshToken()
        }
        const accessToken = await REFRESH_STATE.promise

        if (accessToken) {
          try {
            processQueue(null, accessToken)
          } catch (error) {
            console.error(error)
          }

          originalRequest.headers.Authorization = `Bearer ${localStorage.getItem(
            "access_token"
          )}`

          isRefreshing = false
          return await axios(originalRequest)
        } else {
          await store.dispatch("auth/logout")
        }
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, "")
        await store.dispatch("auth/setTokenData", {} as TokenData)
        await store.dispatch("auth/logout")
        isRefreshing = false
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// window.refreshToken = async () => {
//   if (!REFRESH_STATE.refreshing) {
//     REFRESH_STATE.promise = callApi.refreshToken()
//   }
//   return await REFRESH_STATE.promise
// }
